import {IApplicationFormData} from '../../models/application-form'
import {IStepConfig} from '../../models/step-config'

const AU_MOBILE_REGEX = /^04\d{8}$/

const CREATE_ACCOUNT_PATH = 'create-account'
const VERIFY_MOBILE_PATH = 'verify-mobile'
const APPLICATION_TYPE_PATH = 'application-type'
const CO_APPLICANT_START_PATH = 'profile'
const VERIFIED_ENTRY_PATHS = new Set([CREATE_ACCOUNT_PATH, VERIFY_MOBILE_PATH])

const normalizeMobile = (mobileNumber?: string): string => {
  if (!mobileNumber) {
    return ''
  }

  return mobileNumber.replace(/\D/g, '')
}

const hasValue = (value?: string): boolean => !!value?.trim()
const isCoApplicantWizard = (data: IApplicationFormData): boolean =>
    (data.renterRole ?? '').toLowerCase() === 'co-applicant'

const isInviteWizard = (data: IApplicationFormData): boolean =>
    isCoApplicantWizard(data)

const hasApplicantName = (data: IApplicationFormData): boolean =>
    hasValue(data.fullName) || (hasValue(data.firstName) && hasValue(data.lastName))

const hasInviteContext = (data: IApplicationFormData): boolean =>
     hasValue(data.coApplicantInviteId)

const hasStartStepData = (data: IApplicationFormData): boolean =>
  hasApplicantName(data) &&
  AU_MOBILE_REGEX.test(normalizeMobile(data.mobileNumber)) &&
  data.termsAccepted === true &&
  data.consentSharingAccepted === true

const hasVerifiedDraftContext = (data: IApplicationFormData): boolean =>
  data.isMobileVerified === true && !!data.sessionId?.trim() && Number(data.applicationId) > 0

const hasActiveOtpLock = (data: IApplicationFormData): boolean => {
  if (!data.otpLockedUntilUtc) {
    return false
  }

  const lockedUntilTimestamp = Date.parse(data.otpLockedUntilUtc)
  if (Number.isNaN(lockedUntilTimestamp)) {
    return false
  }

  return lockedUntilTimestamp > Date.now()
}

const isStepAccessible = (stepPath: string, data: IApplicationFormData): boolean => {
  if (isInviteWizard(data)) {
    if (stepPath === CREATE_ACCOUNT_PATH) {
      return !hasVerifiedDraftContext(data) && hasInviteContext(data)
    }

    if (stepPath === VERIFY_MOBILE_PATH) {
      return (
        !hasVerifiedDraftContext(data) &&
        hasInviteContext(data) &&
        hasStartStepData(data) &&
        !hasActiveOtpLock(data)
      )
    }

    return hasVerifiedDraftContext(data)
  }

  if (stepPath === CREATE_ACCOUNT_PATH) {
    return true
  }

  if (stepPath === VERIFY_MOBILE_PATH) {
    return hasStartStepData(data) && !hasActiveOtpLock(data)
  }

  return hasVerifiedDraftContext(data)
}

const getUnknownStepFallback = (activeSteps: IStepConfig[], data: IApplicationFormData): string => {
    if (isInviteWizard(data)) {
        const preferredPath = hasVerifiedDraftContext(data)
            ? CO_APPLICANT_START_PATH
            : CREATE_ACCOUNT_PATH
        return (
            activeSteps.find((step) => step.path === preferredPath)?.path ??
            activeSteps[0]?.path ??
            preferredPath
        )
    }

    const preferredPath = hasVerifiedDraftContext(data)
        ? APPLICATION_TYPE_PATH
        : CREATE_ACCOUNT_PATH
    return (
        activeSteps.find((step) => step.path === preferredPath)?.path ??
        activeSteps[0]?.path ??
        CREATE_ACCOUNT_PATH
    )
}

export const resolveWizardResumePath = (
  activeSteps: IStepConfig[],
  data: IApplicationFormData
): string | null => {
  const savedStepKey = data.stepKey?.trim()
  if (!savedStepKey) {
    return null
  }

  const savedStep = activeSteps.find(
    (step) => step.key === savedStepKey || step.path === savedStepKey
  )

  if (!savedStep) {
    return null
  }

  if (hasVerifiedDraftContext(data) && VERIFIED_ENTRY_PATHS.has(savedStep.path)) {
    return getUnknownStepFallback(activeSteps, data)
  }

  const savedStepIndex = activeSteps.indexOf(savedStep)
  const resumeStep = activeSteps[savedStepIndex + 1] ?? savedStep

  return isStepAccessible(resumeStep.path, data) ? resumeStep.path : null
}

export const resolveWizardRedirectPath = ({
  stepParam,
  currentStepPath,
  activeSteps,
  data,
}: {
  stepParam?: string
  currentStepPath?: string
  activeSteps: IStepConfig[]
  data: IApplicationFormData
}): string | null => {
  if (!currentStepPath) {
    const fallbackPath = getUnknownStepFallback(activeSteps, data)
    return fallbackPath === stepParam ? null : fallbackPath
  }

  if (!isStepAccessible(currentStepPath, data)) {
    const fallbackPath = getUnknownStepFallback(activeSteps, data)
    return stepParam === fallbackPath ? null : fallbackPath
  }

  return null
}

export const toOptionalBoolean = (value: unknown): boolean | undefined => {
  if (value === true || value === false) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') {
      return true
    }
    if (normalized === 'false') {
      return false
    }
  }

  return undefined
}

export const toOptionalNumeric = (value: unknown, defaultValue?: number): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim()

    if (normalized === '') {
      return defaultValue
    }

    const parsed = Number(normalized)

    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  return defaultValue
}
